import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import AdminLogin from './pages/AdminLogin';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminDashboard from './pages/AdminDashboard';
import BookingPage from './pages/BookingPage';
import VoucherPurchasePage from './pages/VoucherPurchasePage';
import TravelCompanionPage from './pages/TravelCompanionPage';
import FlightRequestPage from './pages/FlightRequestPage';
import ThankYouPage from './pages/ThankYouPage';
import PassengerWeightsPage from './pages/PassengerWeightsPage';
import PilotRegistrationPage from './pages/PilotRegistrationPage';
import PilotPublicProfilePage from './pages/PilotPublicProfilePage';
import Gallery from './pages/Gallery';
import FlightSafetyPage from './pages/FlightSafetyPage';
import EasaCharterPage from './pages/EasaCharterPage';
import SafetyForumPage from './pages/SafetyForumPage';
import OwnerLoginPage from './pages/OwnerLoginPage';
import OwnerPortalPage from './pages/OwnerPortalPage';
import OwnerRegisterPage from './pages/OwnerRegisterPage';
import AircraftOwnersLandingPage from './pages/AircraftOwnersLandingPage';
import PartnerDashboardPage from './pages/PartnerDashboardPage';
import LogoShowcase from './pages/LogoShowcase';
import { AuthProvider, useAuth } from './context/AuthContext';
import { loginUrlWithReturn } from './utils/safeReturnUrl';
import { captureAttributionFromSearch } from './utils/attribution';
import i18n from './i18n';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading, instructor } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="container auth-route-loading" role="status" aria-live="polite">
        <p>{i18n.t('admin.loading')}</p>
      </div>
    );
  }
  if (!isAuthenticated) {
    const dest = `${location.pathname}${location.search || ''}`;
    if (location.pathname.startsWith('/owner/')) {
      return <Navigate to={`/owner/login?returnUrl=${encodeURIComponent(dest)}`} replace />;
    }
    return <Navigate to={loginUrlWithReturn(dest)} replace />;
  }
  const role = (instructor?.accessRole || '').toLowerCase();
  if (location.pathname.startsWith('/admin') && role === 'partner') {
    return <Navigate to="/partner/dashboard" replace />;
  }
  if (location.pathname.startsWith('/admin') && role === 'owner') {
    return <Navigate to="/owner/dashboard" replace />;
  }
  if (location.pathname.startsWith('/owner/') && role && role !== 'owner') {
    return <Navigate to="/admin" replace />;
  }
  if (location.pathname.startsWith('/partner/') && role && role !== 'partner') {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

const AttributionCapture = () => {
  const location = useLocation();

  React.useEffect(() => {
    captureAttributionFromSearch(location.search);
  }, [location.search]);

  return null;
};

const RouteAnalytics = () => {
  const location = useLocation();

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('event', 'page_view', {
      page_path: `${location.pathname}${location.search || ''}`,
      page_title: document.title
    });
  }, [location.pathname, location.search]);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AttributionCapture />
          <RouteAnalytics />
          <Navbar />
          <main className="App-main">
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/legenda" element={<Home variant="legenda" />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/booking/voucher" element={<VoucherPurchasePage />} />
            <Route path="/booking/companions" element={<TravelCompanionPage />} />
            <Route path="/booking/request-flight" element={<FlightRequestPage />} />
            <Route path="/booking/thank-you" element={<ThankYouPage />} />
            <Route path="/booking/passenger-weights/:token" element={<PassengerWeightsPage />} />
            <Route path="/pilot/register" element={<PilotRegistrationPage />} />
            <Route path="/pilot/:id" element={<PilotPublicProfilePage />} />
            <Route path="/pilota/:id" element={<PilotPublicProfilePage />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/flight-safety" element={<FlightSafetyPage />} />
            <Route path="/easa-charter" element={<EasaCharterPage />} />
            <Route path="/safety-forum" element={<PrivateRoute><SafetyForumPage /></PrivateRoute>} />
            <Route path="/owner/login" element={<OwnerLoginPage />} />
            <Route path="/owner/register" element={<OwnerRegisterPage />} />
            <Route path="/aircraft-owners" element={<AircraftOwnersLandingPage />} />
            <Route path="/owner/dashboard" element={<PrivateRoute><OwnerPortalPage view="dashboard" /></PrivateRoute>} />
            <Route path="/owner/profile" element={<PrivateRoute><OwnerPortalPage view="profile" /></PrivateRoute>} />
            <Route path="/owner/aircraft" element={<PrivateRoute><OwnerPortalPage view="aircraft" /></PrivateRoute>} />
            <Route path="/partner/dashboard" element={<PrivateRoute><PartnerDashboardPage /></PrivateRoute>} />
            <Route path="/logo-showcase" element={<LogoShowcase />} />
            <Route path="/foglalas" element={<Navigate to="/booking" replace />} />
            <Route path="/utitars" element={<Navigate to="/booking/companions" replace />} />
            <Route path="/foglalas/voucher" element={<Navigate to="/booking/voucher" replace />} />
            <Route path="/foglalas/koszonjuk" element={<Navigate to="/booking/thank-you" replace />} />
            <Route path="/pilota/regisztracio" element={<Navigate to="/pilot/register" replace />} />
            <Route path="/regisztracio/repulogep-tulajdonos" element={<Navigate to="/owner/register" replace />} />
            <Route path="/repulogep-tulajdonosok" element={<Navigate to="/aircraft-owners" replace />} />
            <Route path="/regisztracio/pilota" element={<Navigate to="/pilot/register" replace />} />
            <Route path="/regisztracio/utas" element={<Navigate to="/booking" replace />} />
            <Route path="/passenger/register" element={<Navigate to="/booking" replace />} />
            <Route path="/utas/regisztracio" element={<Navigate to="/booking" replace />} />
            <Route path="/galeria" element={<Navigate to="/gallery" replace />} />
            <Route path="/repulesbiztonsag" element={<Navigate to="/flight-safety" replace />} />
            <Route path="/easa-charta" element={<Navigate to="/easa-charter" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
            <Route 
              path="/admin" 
              element={
                <PrivateRoute>
                  <AdminDashboard />
                </PrivateRoute>
              } 
            />
          </Routes>
          </main>
          <Footer />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
